import { ObjectiveFollowup, useObjectives } from '../components/ObjectiveFollowup';
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { api } from '../api/client';
import { EvolutionTable, type MatrixSession, type TrendScores } from '../components/EvolutionTable';
import {
  SCORE_DIMENSIONS, TAKES_CARDS, HORIZON_LABELS,
  type ScoreLetter, type DimLang, type Horizon,
} from '../../shared/scoreDimensions';
import { htmlToText } from '../lib/html';
import type { CardCatalog } from '../types';
import { useT } from '../i18n';

interface PickInfo {
  topic_id: number;
  pick_order: number;
  name: string;
  color: string | null;
  pdf_card_number: number | null;
  context: string | null;
  horizon: Horizon | null;
  offering_names: string[];
}

type Scores = Record<(typeof SCORE_DIMENSIONS)[number]['key'], ScoreLetter | null>;

interface SessionEntry {
  session_id: number;
  scheduled_date: string;
  title: string | null;
  location: string | null;
  session_type: 'th' | 'bsp' | 'intake_bsp' | 'journal_import';
  presence: string;
  animator_name: string | null;
  updated_by_name: string | null;
  context_notes: string | null;
  defi: string | null;
  takes_cards: boolean | null;
  scores: Scores;
  picks: PickInfo[];
}

interface LocalBsp {
  objectives_set: string;
  bsp_date: string | null;
}

interface OcrBspObjectives {
  objectifs: string[];
  moyens: string[];
  uploaded_at: string | null;
  filename: string | null;
}

interface Objectives {
  bsp: OcrBspObjectives | null;
  intake_goals: { request_date: string | null; goals: string[] }[];
  journal: { entry_date: string | null; content: string }[];
}

interface IntakeInfo {
  request_date: string | null;
  notes: string | null;
  goals_in_words: string | null;
  situation_formation: string | null;
  situation_health: string | null;
  situation_psychological: string | null;
  situation_family: string | null;
  situation_medication: string | null;
  hobbies: string | null;
  availability: string | null;
}

interface Detail {
  display_name: string;
  v2_participant_id: number | null;
  stats: {
    last_session_date: string | null;
    next_session_date: string | null;
    attendance_count: number;
    sessions_count: number;
  };
  sessions: SessionEntry[];   // newest first
  local_bsp: LocalBsp | null;
  objectives: Objectives | null;
  intake_info: IntakeInfo | null;
  v2_available: boolean;
}

interface Props {
  v2Id: number | null;
  participantName: string | null;
  onBack: () => void;
}

function localIsoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ParticipantDetailPage({ v2Id, participantName, onBack }: Props) {
  const goals = useObjectives(v2Id);
  const { t, lang } = useT();
  const dl = lang as DimLang;
  const [data, setData] = useState<Detail | null>(null);
  const [catalog, setCatalog] = useState<CardCatalog | null>(null);
  const [synth, setSynth] = useState<{ status: string; syntheses: Record<number, string> } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<CardCatalog>('/api/cards').then(setCatalog).catch(() => setCatalog({ categories: [], cards: [] }));
  }, []);

  useEffect(() => {
    const qs = v2Id != null ? `v2_id=${v2Id}` : `name=${encodeURIComponent(participantName ?? '')}`;
    setData(null);
    setError(null);
    api.get<Detail>(`/api/my-participants/detail?${qs}`)
      .then(setData)
      .catch((e) => setError(String(e).includes('404') ? t('participant.notFound') : String(e)));
    // per-card LLM syntheses (cached server-side; 'pending' while a fresh run writes)
    setSynth(null);
    const load = (wait: boolean) => api.get<{ status: string; syntheses: Record<number, string> }>(`/api/participants/synthesis?${qs}&wait=${wait ? 1 : 0}`)
      .then((r) => { setSynth(r); if (r.status === 'pending') setTimeout(() => load(true), 1500); })
      .catch(() => setSynth({ status: 'error', syntheses: {} }));
    load(false);
  }, [v2Id, participantName]);  // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };
  const formatShort = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  // The shared evolution surface wants sessions oldest → newest; the API
  // returns them newest first. The most recent session gets the "today"
  // accent only when it actually is today's.
  const chrono = useMemo(() => {
    if (!data) return { sessions: [] as MatrixSession[], series: [] as TrendScores[], todayLast: false };
    const asc = [...data.sessions].reverse();
    const todayIso = localIsoToday();
    const todayLast = asc.length > 0 && asc[asc.length - 1].scheduled_date === todayIso;
    const sessions: MatrixSession[] = asc.map((s, i) => ({
      session_id: s.session_id,
      scheduled_date: s.scheduled_date,
      title: s.title,
      isToday: todayLast && i === asc.length - 1,
      picks: s.picks.map((p) => ({
        topic_id: p.topic_id,
        name: p.name,
        category_color: p.color,
        context: p.context,
        horizon: p.horizon,
      })),
    }));
    const series: TrendScores[] = asc.map((s) => ({ ...s.scores, takes_cards: s.takes_cards }));
    return { sessions, series, todayLast };
  }, [data]);

  if (error) {
    return (
      <div>
        <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginBottom: 16 }}>
          <ArrowLeft size={14} /> {t('common.back')}
        </button>
        <div className="empty-state">{error}</div>
      </div>
    );
  }
  if (!data) return <div className="empty-state">{t('common.loading')}</div>;

  const obj = data.objectives;
  const hasLocalBsp = !!data.local_bsp;
  const hasGoalsInWords = !!data.intake_info?.goals_in_words
    && data.intake_info.goals_in_words.trim().length > 0;
  const hasOcrBsp = !!obj?.bsp && (obj.bsp.objectifs.length > 0 || obj.bsp.moyens.length > 0);
  const hasIntakeGoals = (obj?.intake_goals.length ?? 0) > 0;
  const hasJournal = (obj?.journal.length ?? 0) > 0;
  const hasAnyObjective = hasLocalBsp || hasGoalsInWords || hasOcrBsp || hasIntakeGoals || hasJournal;

  const info = data.intake_info;
  const infoFields: { key: string; value: string | null; html?: boolean }[] = info ? [
    { key: 'participant.info.notes', value: info.notes, html: true },
    { key: 'participant.info.formation', value: info.situation_formation },
    { key: 'participant.info.health', value: info.situation_health },
    { key: 'participant.info.psychological', value: info.situation_psychological },
    { key: 'participant.info.family', value: info.situation_family },
    { key: 'participant.info.medication', value: info.situation_medication },
    { key: 'participant.info.hobbies', value: info.hobbies },
    { key: 'participant.info.availability', value: info.availability },
  ].filter((f) => f.value != null && f.value.trim().length > 0) : [];

  const firstName = data.display_name.split(' ')[0];
  const hasAnyScore = (s: SessionEntry) =>
    s.takes_cards != null || SCORE_DIMENSIONS.some((d) => s.scores[d.key] != null);
  /** "saisi par X" when the writer is not the séance's animator. */
  const recordedBy = (s: SessionEntry) =>
    s.updated_by_name && s.updated_by_name !== s.animator_name ? s.updated_by_name : null;

  return (
    <div className="pd">
      {/* ── Portrait header: identity + the key dates woven in ── */}
      <div className="pd-head">
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <ArrowLeft size={14} /> {t('common.back')}
        </button>
        <div className="pd-head-main">
          <h1 className="pd-name">{data.display_name}</h1>
          <div className="pd-meta">
            <span className="pd-meta-item">
              <strong>{data.stats.attendance_count}</strong> {t('participants.attendance').toLowerCase()}
            </span>
            <span className="pd-meta-sep" aria-hidden>·</span>
            <span className="pd-meta-item">
              {t('participants.lastSession')}{' '}
              <strong>{data.stats.last_session_date ? formatShort(data.stats.last_session_date) : '—'}</strong>
            </span>
            <span className="pd-meta-sep" aria-hidden>·</span>
            <span className="pd-meta-item">
              {t('participants.nextSession')}{' '}
              <strong>{data.stats.next_session_date ? formatShort(data.stats.next_session_date) : '—'}</strong>
            </span>
          </div>
        </div>
      </div>

      <ObjectiveFollowup {...goals} />
      {/* ── Objectifs — the participant's own words as the anchor ── */}
      <section className="pd-section">
        <h2 className="pd-section-title">{t('participant.objectives')}</h2>
        {!data.v2_available && <div className="pt-v2-warning">{t('participant.v2Unavailable')}</div>}
        {data.v2_available && !hasAnyObjective && (
          <div className="empty-state">{t('participant.objectives.empty')}</div>
        )}

        {/* Objectives typed by the animator in this app's BSP — canonical. */}
        {hasLocalBsp && data.local_bsp && (
          <blockquote className="pd-quote">
            <p className="pd-quote-text">{data.local_bsp.objectives_set}</p>
            <footer className="pd-quote-src">
              {t('participant.objectives.local')} — {t('participant.objectives.localSource')}
              {data.local_bsp.bsp_date ? ` · ${formatShort(data.local_bsp.bsp_date)}` : ''}
            </footer>
          </blockquote>
        )}

        {/* Free-text intake goals are source text, never inferred card picks. */}
        {hasGoalsInWords && info && (
          <blockquote className="pd-quote">
            <p className="pd-quote-text">{htmlToText(info.goals_in_words!)}</p>
            <footer className="pd-quote-src">Objectifs du dossier Éveil — texte source
              {info.request_date ? ` · ${formatShort(info.request_date)}` : ''}
            </footer>
          </blockquote>
        )}

        {hasOcrBsp && obj?.bsp && (
          <div className="pd-block">
            <div className="pd-block-title">
              {t('participant.objectives.own')}
              <span className="pt-source">
                {t('participant.objectives.ownSource')}
                {obj.bsp.uploaded_at ? ` · ${formatShort(obj.bsp.uploaded_at.slice(0, 10))}` : ''}
              </span>
            </div>
            {obj.bsp.objectifs.length > 0 && (
              <ul className="pt-list">
                {obj.bsp.objectifs.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            )}
            {obj.bsp.moyens.length > 0 && (
              <>
                <div className="field-label" style={{ marginTop: 12, marginBottom: 4 }}>
                  {t('participant.objectives.means')}
                </div>
                <ul className="pt-list">
                  {obj.bsp.moyens.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </>
            )}
          </div>
        )}

        {hasIntakeGoals && obj && (
          <div className="pd-block">
            <div className="pd-block-title">{t('participant.objectives.structured')}</div>
            {obj.intake_goals.map((g, i) => (
              <div key={i} className="pd-block-row">
                {g.request_date && <span className="pt-source">{formatShort(g.request_date)}</span>}
                <span className="pick-row">
                  {g.goals.map((label, j) => (
                    <span key={j} className="badge badge-accent">{label}</span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        )}

        {hasJournal && obj && (
          <div className="pd-block">
            <div className="pd-block-title">{t('participant.objectives.journal')}</div>
            {obj.journal.map((j, i) => (
              <div key={i} className="pd-block-row">
                {j.entry_date && <div className="pt-source">{formatShort(j.entry_date)}</div>}
                <div className="pt-text">{htmlToText(j.content)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Premier entretien — accessible, not shouting ── */}
      <section className="pd-section">
        <details className="pd-details">
          <summary className="pd-details-summary">
            <span className="pd-section-title pd-details-title">{t('participant.info')}</span>
            <span className="pt-source">
              {info?.request_date
                ? `${t('participant.info.date')} ${formatShort(info.request_date)}`
                : t('participant.intake.open')}
            </span>
          </summary>
          <div className="pd-details-body">
            {!data.v2_available && <div className="pt-v2-warning">{t('participant.v2Unavailable')}</div>}
            {data.v2_available && infoFields.length === 0 && (
              <div className="empty-state">{t('participant.info.empty')}</div>
            )}
            {infoFields.length > 0 && (
              <div className="pd-info-grid">
                {infoFields.map((f) => (
                  <div key={f.key} className={f.html ? 'pd-info-field pd-info-field--wide' : 'pd-info-field'}>
                    <div className="pd-info-label">{t(f.key)}</div>
                    <div className="pt-text">{f.html ? htmlToText(f.value!) : f.value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </details>
      </section>

      {/* ── L'évolution — same language as the session editor ── */}
      <section className="pd-section">
        <h2 className="pd-section-title">
          {t('editor.evolution').replace('{name}', firstName)}
        </h2>
        <EvolutionTable
          sessions={chrono.sessions}
          series={chrono.series}
          categories={catalog?.categories}
          syntheses={synth?.status === 'ready' ? synth.syntheses : undefined}
        />
        {synth?.status === 'pending' && <div className="evo-note evo-note--pending">{t('evo.synthesisPending')}</div>}
        {synth?.status === 'ready' && Object.keys(synth.syntheses).length > 0 && <div className="evo-note">{t('evo.synthesisNote')}</div>}
        {synth?.status === 'error' && <div className="evo-note">{t('evo.synthesisError')}</div>}
      </section>

      {/* ── Journal des séances — cards (with their context, horizon and
             ateliers), notes, défi and letters per session, plus the premier
             entretien / BSP itself as a meeting entry ── */}
      <section className="pd-section">
        <h2 className="pd-section-title">{t('participant.journal')}</h2>
        {data.sessions.length === 0 && !info?.request_date && (
          <div className="empty-state">{t('participant.journal.empty')}</div>
        )}
        <div className="pd-journal">
          {data.sessions.map((s) => {
            const by = recordedBy(s);
            return (
              <article key={s.session_id} className="pd-entry">
                <div className="pd-entry-head">
                  <a className="pd-entry-date" href={`#session/${s.session_id}`} title="Ouvrir et compléter cette séance">{formatDate(s.scheduled_date)}</a>
                  {s.title && <span className="pd-entry-meta">· {s.title}</span>}
                  {s.session_type === 'bsp' && <span className="badge badge-bsp">{t('upcoming.badge.bilan')}</span>}
                  <span className={`badge ${s.presence === 'absent' ? 'badge-muted pd-badge-absent' : 'badge-muted'}`}>
                    {t(`session.presence.${s.presence}`)}
                  </span>
                  {s.animator_name && (
                    <span className="pd-entry-meta pd-entry-who">
                      · {s.animator_name}
                      {by && <> · <span className="attrib-chip attrib-chip--other">{t('session.recordedBy')} {by}</span></>}
                    </span>
                  )}
                </div>
                {s.picks.length > 0 && (
                  <div className="pick-row pd-entry-picks">
                    {s.picks.map((p) => (
                      <span key={p.topic_id} className="pd-pick" title={p.context ?? p.name}>
                        {/* archived (old-deck) cards have no category: neutral dot */}
                        <span className="pd-pick-dot" style={{ background: p.color ?? 'var(--ink-3)' }} />
                        <span className="pd-pick-name">{p.name}</span>
                        {p.horizon && <span className="pd-pick-h">{HORIZON_LABELS[p.horizon][dl]}</span>}
                        {p.pdf_card_number != null && (
                          <span className="pd-pick-num">{p.pdf_card_number}</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
                {s.picks.some((p) => p.context || p.offering_names.length > 0) && (
                  <ul className="pd-entry-contexts">
                    {s.picks.filter((p) => p.context || p.offering_names.length > 0).map((p) => (
                      <li key={p.topic_id}>
                        <strong>{p.name}</strong>
                        {p.context && <> — {p.context}</>}
                        {p.offering_names.length > 0 && (
                          <span className="pd-pick-ateliers"> · {t('report.ateliers')} {p.offering_names.join(', ')}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {s.context_notes && s.context_notes.trim().length > 0 && (
                  <div className="pd-entry-notes">{htmlToText(s.context_notes)}</div>
                )}
                {s.defi && (
                  <div className="pd-entry-defi"><span className="pd-info-label">{t('editor.defi')}</span> {s.defi}</div>
                )}
                {hasAnyScore(s) && (
                  <div className="pd-entry-obs">
                    {s.takes_cards != null && s.scores.prise_cartes == null && (
                      <span className="pd-obs" title={`${TAKES_CARDS.label[dl]} — ${(s.takes_cards ? TAKES_CARDS.yes : TAKES_CARDS.no).short[dl]}`}>
                        <span className="pd-obs-label">{TAKES_CARDS.reportLabel[dl]}</span>
                        <span className="pd-obs-value">{(s.takes_cards ? TAKES_CARDS.yes : TAKES_CARDS.no).short[dl]}</span>
                      </span>
                    )}
                    {SCORE_DIMENSIONS.map((dim) => {
                      const v = s.scores[dim.key];
                      if (v == null) return null;
                      return (
                        <span
                          key={dim.key}
                          className="pd-obs"
                          title={`${dim.label[dl]} — ${v} · ${dim.levels[v].short[dl]}`}
                        >
                          <span className="pd-obs-label">{dim.reportLabel[dl]}</span>
                          <span className="pd-obs-value">{v}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </article>
            );
          })}

          {/* The premier entretien / BSP as the journal's founding entry (the
              list is newest-first, so the oldest event closes it). */}
          {info?.request_date && (
            <article className="pd-entry">
              <div className="pd-entry-head">
                <span className="pd-entry-date">{formatDate(info.request_date)}</span>
                <span className="badge badge-accent">{t('participant.objectives.intakeSource')}</span>
              </div>
              {hasGoalsInWords && info.goals_in_words && (
                <><div className="pd-info-label">Objectifs du dossier Éveil — texte source</div>
                  <div className="pd-entry-notes">{htmlToText(info.goals_in_words)}</div></>
              )}
            </article>
          )}
        </div>
      </section>
    </div>
  );
}
